import { MobileDrawer } from '@components/MobileDrawer/MobileDrawer';
import { Switch } from '@components/Switch/Switch';
import { GradientScrollDiv } from '@components/util/GradientScrollDiv';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { showToast } from '@lib/utils/toastmaster';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { InputAdornment, Menu, TextField, Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { LibraryStore } from '@stores/Library/Library';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { trackingStore } from '@stores/Tracking';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useRef, useState } from 'react';
import { LibraryIconStack } from './components/LibraryIconStack/LibraryIconStack';
import { LibrarySwitchRow } from './components/LibrarySwitchRow/LibrarySwitchRow';
import css from './LibrarySwitch.module.scss';

/** Destination-mode value: an existing library, or a request to create a new one. */
export type LibraryDestination = { kind: 'existing'; library: LibraryStore } | { kind: 'new' };

/**
 * Ready-made `disabledReasonFor` for pickers whose verbs *read* media — searching a library,
 * or handing one to an Agent. A library with nothing in it is a dead end for both.
 *
 * Deliberately NOT the default for every picker: `AddMediaModal` targets a library to upload
 * *into*, where empty is not only valid but the likeliest case. Opt in per call site.
 *
 * `isEmpty` (see `LibraryStore`) fails open while stats are still loading, and is shared with
 * `multiLibSearch.zeroResultReason` so the picker can't call a library empty while the Moments
 * empty state disagrees.
 */
export const emptyLibraryDisabledReason = (library: LibraryStore): string | undefined =>
  library.isEmpty ? 'This library is empty.' : undefined;

interface LibrarySwitchProps {
  /** 'search' (default) drives the multi-lib search selection. 'destination' is a
   *  single-select picker for an upload target — it never touches search state. */
  mode?: 'search' | 'destination';
  /** Destination mode only: the currently chosen target. */
  destination?: LibraryDestination;
  /** Destination mode only: called when the user picks a library or "+ New library". */
  onDestinationChange?: (next: LibraryDestination) => void;
  /** Destination mode only: show the pinned "+ New library" row (default true).
   *  Pass false for pickers that can only target existing libraries (e.g. the
   *  workspace signposts), so `onDestinationChange` only ever returns 'existing'. */
  allowNewLibrary?: boolean;
  /** Render a compact auto-width trigger chip on mobile too, instead of the
   *  full-width destination pill (which is meant for the AddMedia modal header).
   *  Use when the trigger sits inline in flowing text, e.g. workspace signposts. */
  compactTrigger?: boolean;
  /**
   * Replace the built-in trigger chip with your own control, so an existing button can
   * open this picker instead of growing a second one beside it. The Libraries page's
   * header verb pills use this: the pill *is* the trigger.
   *
   * Call `onClick` with the event — `handleClick` anchors the popover off
   * `event.currentTarget`, so whichever element you pass becomes the anchor. Omit this
   * prop and the default icon-stack chip renders as before.
   */
  trigger?: (args: {
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    open: boolean;
  }) => React.ReactNode;
  /**
   * Override the popover's heading. Destination mode otherwise says "Add to", which only
   * makes sense for an upload target — a picker answering "which library do I search?"
   * needs its own words.
   */
  headerLabel?: string;
  /**
   * Why a given library can't be picked, or `undefined` when it can. A row with a reason
   * renders inert with the reason in place of its stats line (see `LibrarySwitchRow`).
   *
   * The predicate and the wording both live with the caller: "empty" is only a blocker for
   * verbs that read media, and a different caller would block for a different reason and
   * want to say so in its own words.
   */
  disabledReasonFor?: (library: LibraryStore) => string | undefined;
  /**
   * Drop the per-row checkbox. For pickers where choosing a row acts immediately and nothing
   * is ever pre-selected — the Libraries page's header verbs open a chat or a search on the
   * spot — so a control that can only ever render unchecked is noise that implies a
   * selection step which doesn't exist.
   */
  hideSelectionControl?: boolean;
  /**
   * Destination mode only: its presence offers the same "Multi-select" switch the search picker
   * has, so the user can gather several libraries and act on them at once. The switch starts
   * off, and a single click still fires the verb while it stays off.
   *
   * One object rather than three coordinated props, because the three were never independent —
   * a flag without a label rendered a button with no text, and a confirm without a handler
   * closed the popover having done nothing. Presence *is* the "this destination accepts a set"
   * fact, so those states are now unrepresentable.
   *
   * The switch and the gathered set are **local to this popover** — deliberately NOT
   * `libraryManager.multiLibrarySelection` / `selectedLibraryIdsToSearch`, even though the
   * search picker's identical-looking switch drives exactly those. Turning that flag off
   * truncates the persisted search scope to its first library
   * (`LibraryManager.setMultiLibrarySelection`), so sharing it would let this popover quietly
   * rewrite what the search bar is scoped to. The caller commits the set itself, via `onConfirm`.
   *
   * ⚠️ Rows hide their checkbox on mobile (`LibrarySwitchRow`), so the gathering affordance is
   * desktop-only today. Fine for the current consumer — the Libraries nav row is `display: none`
   * on mobile — but a mobile surface for this needs the row fixed first.
   */
  multiSelect?: {
    /** Fired by the footer button with the gathered library ids, in the order picked. */
    onConfirm: (libraryIds: string[]) => void;
    /**
     * That button's label, as a function of how many are gathered — the caller owns the wording
     * and its pluralization, the same way it owns `headerLabel`. Called with 0, so it must
     * return something sensible for the disabled state.
     */
    label: (count: number) => string;
  };
  /**
   * Fired when the picker opens. Lets a caller instrument opening without wrapping `trigger` —
   * which it would otherwise have to do synchronously, or lose `currentTarget` for the anchor.
   */
  onOpen?: () => void;
}

export const LibrarySwitch = observer(
  ({
    mode = 'search',
    destination,
    onDestinationChange,
    allowNewLibrary = true,
    compactTrigger = false,
    trigger,
    headerLabel,
    disabledReasonFor,
    hideSelectionControl = false,
    multiSelect,
    onOpen,
  }: LibrarySwitchProps = {}) => {
    const isDestination = mode === 'destination';
    const isMobile = useIsMobile();
    const libraryManager = useLibraryManager();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [filterByTitle, setFilterByTitle] = useState<string | null>(null);
    const initialSelectedLibraries = useRef<string[]>([]);
    const open = Boolean(anchorEl);

    // Latches on the first open. Gates the row list, which is otherwise built (desktop) or
    // fully mounted (mobile) for a popover the user may never touch — see `filteredLibraryRows`.
    const [everOpened, setEverOpened] = useState(false);

    // Local multi-select, for destination-mode callers that opt in. Kept out of the manager
    // on purpose — see `multiSelect`.
    const [multiSelectLocal, setMultiSelectLocal] = useState(false);
    const [gatheredIds, setGatheredIds] = useState<string[]>([]);
    const isGathering = isDestination && !!multiSelect && multiSelectLocal;

    // One accessor for "is multi-select on", so the `<Switch>` below doesn't re-branch on the
    // mode for its value, its setter and its tracking field.
    const multiSelectOn = isDestination ? multiSelectLocal : libraryManager.multiLibrarySelection;
    const setMultiSelectOn = (next: boolean) => {
      if (isDestination) {
        setMultiSelectLocal(next);
        // Turning it off drops whatever was gathered rather than collapsing to one library:
        // nothing has been committed yet, so there is no selection to preserve — and silently
        // keeping the first pick is how the manager's equivalent surprises people.
        if (!next) setGatheredIds([]);
      } else {
        libraryManager.setMultiLibrarySelection(next);
      }
      trackingStore.libraryMultiSelectToggled({
        enabled: next,
        picker: isDestination ? 'destination' : undefined,
      });
    };

    const toggleGathered = (libraryId: string) =>
      setGatheredIds((current) =>
        current.includes(libraryId)
          ? current.filter((id) => id !== libraryId)
          : [...current, libraryId],
      );

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setEverOpened(true);
      onOpen?.();
      if (isDestination) return;
      initialSelectedLibraries.current = Array.from(libraryManager.selectedLibraryIdsToSearch);
      trackingStore.librarySelectorOpened({
        libraryCount: libraryManager.libraries.length,
        selectedCount: libraryManager.selectedLibraryIdsToSearch.size,
      });
    };

    const handleClose = () => {
      if (isDestination) {
        setAnchorEl(null);
        // Gathered libraries don't survive the popover: the set exists to be committed by the
        // footer button, so leaving it behind would silently re-apply on the next open. The
        // switch itself persists for the session, since that's a way of working rather than a
        // pending action.
        setGatheredIds([]);
        setTimeout(() => setFilterByTitle(null), 300);
        return;
      }
      const finalSelectedLibraries = Array.from(libraryManager.selectedLibraryIdsToSearch);

      // no libraries selected, switch to demo library
      if (finalSelectedLibraries.length === 0) {
        const demoLibrary = libraryManager.demoLibrary;
        if (demoLibrary) {
          libraryManager.toggleLibraryToSearchSelection(demoLibrary.data._id);
          showToast('library-no-selection');
        }
      }
      // libraries selected, check if any changed
      else {
        const hasChanged = !(
          initialSelectedLibraries.current.length === finalSelectedLibraries.length &&
          initialSelectedLibraries.current.every((id) => finalSelectedLibraries.includes(id))
        );

        if (hasChanged) {
          showToast('library-selection-changed');
        }
      }

      const finalSelectedLibrariesForTracking = Array.from(
        libraryManager.selectedLibraryIdsToSearch,
      );
      const hasChangedForTracking = !(
        initialSelectedLibraries.current.length === finalSelectedLibrariesForTracking.length &&
        initialSelectedLibraries.current.every((id) =>
          finalSelectedLibrariesForTracking.includes(id),
        )
      );
      trackingStore.librarySelectorClosed({
        selectedCount: finalSelectedLibrariesForTracking.length,
        selectionChanged: hasChangedForTracking,
        isMultiSelect: libraryManager.multiLibrarySelection,
      });

      setAnchorEl(null);
      setTimeout(() => {
        setFilterByTitle(null);
      }, 300);
    };

    // One definition of "which libraries is the user currently looking at". Was written out
    // three times (the row list plus both halves of the group-selection handler), which is how
    // the handler ended up able to select rows the list renders as unpickable.
    const needle = filterByTitle?.toLowerCase() ?? '';
    const filteredLibraries = libraryManager.libraries.filter((library) =>
      library.data.name.toLowerCase().includes(needle),
    );

    // Which way the bulk control goes. Derived once so the label and the handler can't disagree
    // — the label used to re-derive this, which is the same drift that let the handler select
    // rows the list rendered as unpickable.
    // The `!isDestination` guard is about subscriptions, not correctness: a plain destination
    // picker has no bulk control, so the value is unused — and reading the search selection
    // would subscribe it to a scope it never touches.
    const isBulkDeselect = isGathering
      ? gatheredIds.length > 1
      : !isDestination && libraryManager.selectedLibraryIdsToSearch.size > 1;

    const handleGroupSelection = () => {
      // Bulk-select honours the same rule the rows do: "Select all" used to go straight to the
      // manager and sweep up libraries whose rows were rendered inert, so the disabled state
      // could be bypassed wholesale by the control directly above it. Deselecting is
      // unconditional — clearing a set must always be possible, including entries that are no
      // longer individually pickable.
      //
      // Computed here rather than per render: it is only ever needed on a click, and at render
      // time it would call the caller's predicate once per library for nothing.
      const targets = isBulkDeselect
        ? filteredLibraries
        : filteredLibraries.filter((library) => !disabledReasonFor?.(library));
      const ids = targets.map((library) => library.libraryId);

      // How many libraries the click actually covered. Usually `ids.length`, but the unfiltered
      // select-all path keeps ruled-out libraries that were already selected (see below), so it
      // covers more than `ids` names and reports its own number.
      let coveredCount = ids.length;

      if (isGathering) {
        setGatheredIds((current) =>
          isBulkDeselect
            ? current.filter((id) => !ids.includes(id))
            : [...new Set([...current, ...ids])],
        );
      } else if (filterByTitle) {
        // Filtered: only the visible subset may change, so touch ids one at a time.
        for (const id of ids) {
          if (isBulkDeselect) libraryManager.deselectLibraryToSearch(id);
          else libraryManager.selectLibraryToSearch(id);
        }
      } else {
        // Unfiltered, so the store's bulk methods apply — and they must, because both begin with
        // `selectedLibraryIdsToSearch.clear()`. That clear is load-bearing: the selection is
        // persisted and NOTHING prunes it (`deleteLibrary` drops the store but not the id, and
        // hydration re-adds every persisted id unchecked), while `selectedLibrariesToSearch`
        // filters by `_libraries` — so an id whose library is gone is invisible in the UI yet
        // still counted by `.size`. Iterating the visible libraries instead would leave such an
        // id behind, and "Deselect all" would stop being able to empty the set.
        if (isBulkDeselect) {
          libraryManager.deselectAllLibrariesToSearch();
        } else {
          // Captured before the clear inside `selectAllLibrariesToSearch`, because the rows
          // exempt an already-selected library from being disabled and this has to match: a
          // library that is in the scope and has since become empty must stay in it. Without
          // the exemption "Select all" could *shrink* the selection — and with every library
          // empty it would select nothing at all, flipping the label straight back.
          const alreadySelected = new Set(libraryManager.selectedLibraryIdsToSearch);

          libraryManager.selectAllLibrariesToSearch();
          // `selectAllLibrariesToSearch` adds every library, including any the caller ruled out,
          // so drop those back off — the disabled rows must not be selectable in bulk either.
          for (const library of filteredLibraries) {
            if (disabledReasonFor?.(library) && !alreadySelected.has(library.libraryId)) {
              libraryManager.deselectLibraryToSearch(library.libraryId);
            }
          }

          // The resulting scope, not `ids.length`: the exemption above keeps ruled-out libraries
          // that were already selected, so `ids` would undercount. Pre-branch this path reported
          // `libraries.length` and was exact because everything got selected; reading the store
          // back is how it stays exact now that it doesn't.
          coveredCount = libraryManager.selectedLibraryIdsToSearch.size;
        }
      }

      const params = { libraryCount: coveredCount, filtered: !!filterByTitle };
      if (isBulkDeselect) trackingStore.libraryDeselectAllClicked(params);
      else trackingStore.librarySelectAllClicked(params);
    };

    const formatLibraryName = (libraryName: string, limit: number = 20) => {
      if (libraryName.length > limit) {
        return libraryName.slice(0, limit) + '...';
      }
      return libraryName;
    };

    // Only the built-in trigger shows these, so a caller-supplied trigger skips the work.
    const switchLabel = trigger
      ? ''
      : isDestination
        ? destination?.kind === 'existing'
          ? formatLibraryName(destination.library.data?.name ?? '')
          : 'New library'
        : libraryManager.selectedLibrariesToSearch.length > 1
          ? `Searching ${libraryManager.selectedLibrariesToSearch.length} libraries`
          : libraryManager.selectedLibrariesToSearch.length === 1
            ? formatLibraryName(libraryManager.selectedLibrariesToSearch[0].data?.name)
            : '';

    // The three selection behaviours, named once. Every row prop below is a lookup on this
    // rather than its own re-derivation of the same precedence — five of them used to branch
    // `isGathering ? … : isDestination ? …` independently and had to be kept in step by hand.
    const rowMode: 'gather' | 'pick' | 'search' = isGathering
      ? 'gather'
      : isDestination
        ? 'pick'
        : 'search';

    // Building the rows is the expensive part of this component (a filter, an O(N log N) sort,
    // and N elements each mounting a thumbnail fetch). Gate it on having been opened at least
    // once:
    //
    //  - Desktop `Menu` has `keepMounted: false`, so until then every row was built and then
    //    thrown away by `Modal` on each render of the surrounding page.
    //  - Mobile is worse: `MobileDrawer` → `SwipeableDrawer` hardcodes `keepMounted: true` for
    //    `variant="temporary"`, so the rows really do mount, portaled to `body`, even inside a
    //    container that is `display: none`. The Libraries page's two pickers were mounting 2×N
    //    rows — each firing a preview-token request and an image fetch — for a nav row a phone
    //    can never show.
    //
    // `everOpened` rather than `open`: gating on `open` would blank the list during the 300ms
    // exit transition (the same window `handleClose`'s deferred filter reset exists to protect).
    //
    // This latch only stops rows being built for a picker that was never touched. The cost of
    // the *first* open — a cover fetch per library, for the four rows that fit — is deferred
    // per-row instead, against the picker's own scroller; `open` goes down too because a mobile
    // drawer stays mounted and unclipped after close.
    const filteredLibraryRows = !everOpened
      ? []
      : filteredLibraries
          // Most-recently-modified first — the library you're likeliest to want is the
          // one you just touched, not whatever's alphabetically first. `?? 0` guards the
          // sort against a missing `updatedAt` (backend fields aren't guaranteed).
          //
          // `slice()` first: `filteredLibraries` is shared with the group-selection handler and
          // `sort` mutates in place.
          .slice()
          .sort((a, b) => (b.data?.updatedAt ?? 0) - (a.data?.updatedAt ?? 0))
          .map((library) => {
            const isRowSelected =
              rowMode === 'gather'
                ? gatheredIds.includes(library.libraryId)
                : rowMode === 'pick'
                  ? destination?.kind === 'existing' &&
                    destination.library.libraryId === library.libraryId
                  : libraryManager.selectedLibraryIdsToSearch.has(library.libraryId);

            // A row that is already selected is never disabled, in every mode. Only *adding* a
            // ruled-out library is blocked.
            //
            // The reason differs per mode but the rule doesn't. In `search` and `gather`, a
            // row's click is the only way to *de*select, so disabling a selected row would
            // strand it in the set (and the search selection persists across sessions). In
            // `pick` there is nothing to deselect — but the row IS the current destination, and
            // disabling it both drops its selected highlight and makes it impossible to return
            // to after switching away, while the surface's own CTAs keep acting on it.
            const disabledReason = isRowSelected ? undefined : disabledReasonFor?.(library);

            return (
              <LibrarySwitchRow
                key={library.libraryId}
                library={library}
                pickerOpen={open}
                // Gathering builds a set, so a pick must not dismiss the popover; the footer
                // button is what ends the interaction.
                closeSwitch={rowMode === 'gather' ? () => undefined : handleClose}
                disabledReason={disabledReason}
                // Gathering is multi-select, so the box is square and always shown — the
                // checkbox is the whole affordance there, whatever the caller asked for.
                selectionControl={
                  rowMode === 'gather'
                    ? 'multi'
                    : hideSelectionControl
                      ? 'none'
                      : rowMode === 'pick' || !libraryManager.multiLibrarySelection
                        ? 'single'
                        : 'multi'
                }
                isSelected={isRowSelected}
                onSelect={
                  rowMode === 'gather'
                    ? () => toggleGathered(library.libraryId)
                    : rowMode === 'pick'
                      ? () => onDestinationChange?.({ kind: 'existing', library })
                      : undefined
                }
              />
            );
          });

    // Destination mode: a pinned row to create a brand-new library as the target.
    // Selecting it hands a 'new' destination back up; the name is entered inline on
    // the upload screen, not here.
    const newLibraryRow =
      isDestination && allowNewLibrary ? (
        <div className={css.newLibraryFooter}>
          <div
            className={classNames(css.newLibraryRow, {
              [css.newLibraryRowSelected]: destination?.kind === 'new',
            })}
            onClick={() => {
              onDestinationChange?.({ kind: 'new' });
              handleClose();
            }}
          >
            <AddIcon />
            <span>New library</span>
          </div>
        </div>
      ) : null;

    // Gathering has to end in an explicit commit — with several libraries checked there is no
    // last click that could stand in for "done". Disabled at zero rather than hidden, so the
    // row of checkboxes has a visible destination from the moment the switch goes on.
    const confirmMultipleRow =
      isGathering && multiSelect ? (
        <div className={css.confirmFooter}>
          <ActionButton
            // Its own prop rather than a width override here: `ActionButton` styles both the
            // wrapper and the button for this, which a single `width: 100%` wouldn't reach.
            fullWidth
            disabled={gatheredIds.length === 0}
            onClick={() => {
              if (gatheredIds.length === 0) return;
              // Hands back ids, not stores: the only consumer feeds them straight to
              // `findMoments`, which resolves them itself. Resolving here just to have the
              // caller map back to ids was two extra hops and a second copy of the lookup.
              multiSelect.onConfirm(gatheredIds);
              handleClose();
            }}
          >
            {multiSelect.label(gatheredIds.length)}
          </ActionButton>
        </div>
      ) : null;

    const headerContent = (
      <div className={css.headerContainer}>
        <div className={css.header} key="librarySwitchHeader">
          <div className={css.librariesLabel}>
            {headerLabel ?? (isDestination ? 'Add to' : 'Libraries')}
          </div>
          {/* Search mode drives the manager's persisted selection mode. Destination mode is
              single-select unless the caller opts into gathering, which runs on local state
              instead — same control, deliberately not the same store (see `multiSelect`). */}
          {(!isDestination || multiSelect) && (
            <>
              <div className={css.multiSelectLabel}>Multi-select</div>
              <Switch checked={multiSelectOn} onChange={() => setMultiSelectOn(!multiSelectOn)} />
              <Tooltip
                title="Toggle between single or multiple library selection"
                arrow
                placement="top"
              >
                <InfoRoundedIcon sx={{ fontSize: 20, color: 'rgba(193, 193, 194, 1)' }} />
              </Tooltip>
            </>
          )}
        </div>
        <TextField
          placeholder="Search libraries..."
          variant="outlined"
          size="small"
          fullWidth
          autoFocus
          onChange={(e) => {
            setFilterByTitle(e.target.value);
            if (e.target.value) {
              trackingStore.libraryFilterSearched({ query: e.target.value });
            }
          }}
          autoComplete="off"
          onKeyDown={(event) => event.stopPropagation()}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 20 }} />
                </InputAdornment>
              ),
            },
          }}
        />
        {/* Wherever multi-select is active — the search picker's persisted mode or a
            destination picker's local gathering — the bulk control comes with it. Picking
            libraries one at a time is exactly the chore this exists to avoid. */}
        {multiSelectOn && (
          <div>
            <div className={css.selectionLabel} onClick={handleGroupSelection}>
              {isBulkDeselect ? 'Deselect all' : 'Select all'}
            </div>
          </div>
        )}
      </div>
    );

    // In destination mode the trigger shows just the chosen library's icon (or none
    // for a new library); in search mode it stacks all selected libraries.
    const triggerLibraries = isDestination
      ? destination?.kind === 'existing'
        ? [destination.library]
        : []
      : libraryManager.selectedLibrariesToSearch;

    // A caller-supplied trigger replaces the chip entirely — it gets `handleClick`, which
    // anchors the popover to whatever element it was fired from, so the menu lands under
    // the caller's own control with no extra wiring. Skip building the chip in that case;
    // it would only be discarded.
    const defaultTrigger = trigger ? null : (
      <div
        className={classNames(css.container, {
          [css.destinationTrigger]: isDestination,
          [css.compactTrigger]: compactTrigger,
        })}
        onClick={handleClick}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {isDestination && destination?.kind === 'new' ? (
          // New-library target: a plus badge in place of the library icon, so it
          // reads as "new" and stays visible on mobile (no thumbnail to show).
          <span className={css.newLibraryTriggerIcon}>
            <AddIcon />
          </span>
        ) : (
          (triggerLibraries.length > 0 || !isDestination) && (
            <LibraryIconStack
              libraries={
                triggerLibraries as (LibraryStore & {
                  data: NonNullable<LibraryStore['data']>;
                })[]
              }
              maxVisible={2}
              size={24}
            />
          )
        )}
        <div className={css.libraryName}>{switchLabel}</div>
        <ExpandMoreIcon className={css.expandIcon} />
      </div>
    );

    return (
      <div
        className={classNames({
          [css.destinationRoot]: isDestination && !trigger,
          [css.compactRoot]: compactTrigger && !trigger,
        })}
      >
        {trigger ? trigger({ onClick: handleClick, open }) : defaultTrigger}
        {isMobile ? (
          <MobileDrawer
            open={open}
            onClose={handleClose}
            frosted
            onOpen={() => {
              if (!anchorEl) {
                setAnchorEl(document.body as HTMLElement);
              }
            }}
          >
            {headerContent}
            <div onTouchMove={(e) => e.stopPropagation()}>
              <GradientScrollDiv
                className={css.mobileScrollOuter}
                innerDivClassName={css.mobileScroll}
              >
                {filteredLibraryRows}
              </GradientScrollDiv>
            </div>
            {newLibraryRow}
            {confirmMultipleRow}
          </MobileDrawer>
        ) : (
          <Menu
            id="userMenu"
            className={css.menu}
            classes={{ paper: css.menuPaper }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            {headerContent}
            <div className={css.scroll}>
              <GradientScrollDiv innerDivClassName={css.gradientInner}>
                {filteredLibraryRows}
              </GradientScrollDiv>
            </div>
            {newLibraryRow}
            {confirmMultipleRow}
          </Menu>
        )}
      </div>
    );
  },
);
